import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Award, BookOpen, Palette, GraduationCap, MapPin } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { SectionHeading } from "@/components/site/SectionHeading";
import { Sunflower, SunflowerMini } from "@/components/site/Sunflower";
import { NADA_PHOTO_URL, IBCCES_STAMP_URL } from "@/lib/assets";

const ABOUT_IMG = NADA_PHOTO_URL;

const roles = [
  { icon: GraduationCap, title: "Kindergarten Principal", body: "21 years in early childhood leadership", color: "bg-brand-sun/25 text-brand-sunDeep" },
  { icon: BookOpen, title: "Curriculum Coordinator", body: "Designing inclusive early-years programs", color: "bg-brand-leaf/20 text-brand-leafDeep" },
  { icon: Award, title: "Autism Certified (IBCCES)", body: "Internationally recognised credential", color: "bg-brand-terracotta/20 text-brand-rust" },
  { icon: Palette, title: "Illustrator", body: "Children's book creator & author", color: "bg-brand-sky/40 text-brand-leafDeep" },
];

const impact = [
  { n: "21", label: "Years as Kindergarten Principal & Curriculum Coordinator" },
  { n: "10", label: "Years specialising in neurodivergency education" },
  { n: "300+", label: "Families coached across Dubai and online" },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="section-pad relative overflow-hidden petal-bg">
        <div className="absolute -top-16 -right-20 opacity-40 pointer-events-none hidden md:block">
          <Sunflower size={260} className="animate-slowspin" />
        </div>
        <div className="relative grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase text-brand-leafDeep bg-brand-honey/50 border border-brand-sun/40 rounded-full px-4 py-1.5 mb-6 w-fit">
              <SunflowerMini size={18} />
              About Nada Zahabi
            </span>
            <h1 className="font-heading font-normal text-[2.75rem] sm:text-5xl lg:text-[4.25rem] leading-[1.02] tracking-tight">
              An educator, a specialist,
              <br />
              and a <span className="sunray-underline">calm presence</span> for
              neurodivergent children.
            </h1>

            <div className="mt-7 flex flex-wrap gap-3 items-center">
              <span className="inline-flex items-center gap-2 text-sm bg-white border-2 border-brand-line rounded-full px-4 py-2 text-brand-leafDeep font-body">
                <span className="text-lg leading-none">🇨🇦</span> Canadian educator
              </span>
              <span className="inline-flex items-center gap-2 text-sm bg-white border-2 border-brand-line rounded-full px-4 py-2 text-brand-leafDeep font-body">
                <MapPin size={14} className="text-brand-sunDeep" /> Based in Dubai
              </span>
              <span className="inline-flex items-center gap-2 text-sm bg-white border-2 border-brand-sun/40 rounded-full px-4 py-2 text-brand-leafDeep font-body font-medium">
                <Award size={14} className="text-brand-sunDeep" /> IBCCES Autism Certified
              </span>
            </div>

            <p className="mt-7 text-brand-muted text-base md:text-lg leading-relaxed max-w-2xl font-body">
              A <span className="font-semibold text-brand-leafDeep">Canadian educator</span> based in Dubai, Nada has spent <span className="font-semibold text-brand-leafDeep">21 years as a Kindergarten Principal and Curriculum Coordinator</span>, with the last <span className="font-semibold text-brand-leafDeep">10 years specialising in neurodivergency education</span>. Her work at Nada's Kids Garden, an early-childhood leadership, IBCCES-certified autism specialist, and her own illustrated children's books and curriculum development, is one grounded, structured practice families and schools can rely on.
            </p>
          </div>
          <div className="lg:col-span-5">
            <Reveal>
              <div className="relative">
                <div className="rounded-full overflow-hidden border-4 border-brand-sun/40 shadow-earthyLg aspect-square bg-white">
                  <img
                    data-testid="about-nada-photo"
                    src={ABOUT_IMG}
                    alt="Nada Zahabi — IBCCES Autism Certified Specialist"
                    className="w-full h-full object-contain"
                  />
                </div>
                {/* Floating IBCCES stamp */}
                <div className="absolute -bottom-4 -right-4 md:-bottom-6 md:-right-6 h-28 w-28 md:h-36 md:w-36 rounded-full bg-white border-4 border-brand-sun shadow-sunny grid place-items-center p-2">
                  <img
                    data-testid="about-ibcces-stamp"
                    src={IBCCES_STAMP_URL}
                    alt="IBCCES Autism Certificate"
                    className="h-full w-full object-contain"
                  />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="section-pad bg-brand-ivory">
        <SectionHeading
          eyebrow="Roles"
          title="Four practices, one philosophy."
        />
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {roles.map((r, i) => (
            <Reveal key={r.title} delay={i * 0.08}>
              <div className="bg-white rounded-[28px] p-8 border-2 border-brand-line hover:border-brand-sun/50 h-full transition-colors">
                <div className={`h-12 w-12 rounded-2xl ${r.color} grid place-items-center mb-6`}>
                  <r.icon size={22} />
                </div>
                <h3 className="font-heading text-xl mb-1">{r.title}</h3>
                <p className="text-sm text-brand-muted font-body">{r.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="section-pad">
        <div className="grid lg:grid-cols-2 gap-14">
          <div>
            <SectionHeading
              eyebrow="Mission"
              title="To move children from surviving to participating."
              intro="Regulation is not a luxury. It is the foundation every other skill is built on. Nada works alongside parents and schools to make that foundation strong — so children can play, learn, and belong."
            />
          </div>
          <div>
            <SectionHeading
              eyebrow="Philosophy"
              title="Supporting the child means supporting the system around them."
              intro="A child doesn't regulate alone. They regulate through the adults, routines, and spaces around them. That's why Nada's work always includes the caregivers, the classroom, and the home — not just the child."
            />
          </div>
        </div>
      </section>

      <section className="section-pad bg-brand-leafDeep text-brand-cream grain relative overflow-hidden">
        <div className="absolute -bottom-16 -left-16 opacity-25 pointer-events-none">
          <Sunflower size={260} showLeaves className="animate-slowspin" />
        </div>
        <div className="relative z-10 grid md:grid-cols-3 gap-8">
          {impact.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.1}>
              <div className="border border-white/15 rounded-[28px] p-10 bg-white/[0.04]">
                <p className="font-brand text-6xl md:text-7xl text-brand-sun leading-none">{s.n}</p>
                <p className="mt-5 text-sm text-brand-cream/85 font-body leading-relaxed">{s.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="section-pad">
        <div className="bg-gradient-to-br from-brand-honey/50 to-brand-leafSoft/30 rounded-[32px] p-10 md:p-14 border-2 border-brand-sun/30 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="font-heading text-2xl md:text-3xl">
              Want to work with Nada directly?
            </h3>
            <p className="text-sm text-brand-muted mt-2 font-body">
              Most parents start with a 30-minute discovery session.
            </p>
          </div>
          <Link
            data-testid="about-book-session-btn"
            to="/contact"
            className="rounded-full bg-brand-ink text-brand-cream px-7 py-3.5 text-sm font-medium inline-flex items-center gap-2 hover:bg-brand-leafDeep transition-colors shadow-earthy"
          >
            Contact <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
